/**
* @Description Master page layout for the application.
* Initial file has been taken from mux-template.
* NOTE - COMPLETE DOCUMENTATION IS NOT AVAILABLE
* @FileName layout.js
* @Author  Arnab Sutar - sutaarn
* @CreatedOn 08 January, 2021 14:28:52
* @IssueID - 459
*/
import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  SideNav,
  Footer,
  MainNavItem,
  SubNavItem,
  Modal,
  SubNavDropdown,
  UtilityHeader,
  HEADER_VARIANT,
  HeaderButton,
  MenuItem,
  SignInButton,
  LangButton,
  useSkipLinks,
  colors,
} from '@manulife/mux';
import { useDispatch, useSelector } from 'react-redux';
import { Email, Phone, Login1 } from '@manulife/mux-cds-icons';
import { checkDirtyData, getRBACData, releaseLockCombo, getSystemConfig, usersDataProcessing,
  getBatchProcessingState, resetStore, getLevelRBACData, getUserPrefData, getUserContactInfo } from '../../redux/actions/commonActions';
import {
  HeaderWrapper,
  LayoutContainer,
  LayoutContent,
  LayoutWrapper,
  SideNavWrapper,
} from './styledComponents';
import { setCurrentLocale, t } from '../../i18n';
import { staticCommonLabelKeys, userLock, globalConstants } from '../../moduleConstants';
import { FasatActionBtn, FasatButton, FasatModal } from '..';
import { clearToken, getLabelErr as getLabelErr1 } from '../../util';
import logger from '../../util/appLogger';
import UserContext from '../../../../userContext';
import staticCommonLabel from '../../staticCommonLabel';
import UserPrefLang from './userPrefLang';
import CancelPopup from '../../../modules/admin/components/cancelPopup/cancelPopup';

const sysConfigPayload = [globalConstants.FASAT_DEFAULT_SIGNOUT_LIMIT, globalConstants.WEB_BATCH_EXPIRY_DAYS,
  globalConstants.FASAT_DEFAULT_DATEFORMAT, globalConstants.DOWNLOAD_CENTRE_REFRESH_INTERVAL_ONLINE,
  globalConstants.FPA_THRESHOLD_TIME_LIMIT, globalConstants.FPA_PP_THRESHOLD_TIME_LIMIT,
  globalConstants.FASAT_EXTERNAL_DEFAULT_SIGNOUT_LIMIT, globalConstants.FASAT_EXTERNAL_MAX_SIGNOUT_LIMIT, globalConstants.FASAT_INTERNAL_MAX_SIGNOUT_LIMIT, globalConstants.FASAT_INTERNAL_CALLING_TIME];

const USER_LAN_SAVE = 'USER_LAN_SAVE';
const USER_LAN_CANCEL = 'USER_LAN_CANCEL';

const getLabelErr = (userPrefError, tr, labelErr, label) => {
  if (userPrefError) {
    return tr(labelErr);
  }
  return tr(label);
};
const { external } = globalConstants;
const curYear = new Date().getFullYear();
const getFooter = ({ userPrefError: err, t: tr }) => (
  <section data-skip-link="Skip to Footer" id="footer-id-react-aria-3-a-privacy">
    <Footer
      items={[{
        ariaLabel: getLabelErr(err, tr, staticCommonLabelKeys.LABEL_LEGAL_ERR, staticCommonLabelKeys.COMMON_LABEL_LEGAL),
        href: 'https://www.manulife.com/en/legal.html',
        id: 'legal',
        isExternal: true,
        label: getLabelErr(err, tr, staticCommonLabelKeys.LABEL_LEGAL_ERR, staticCommonLabelKeys.COMMON_LABEL_LEGAL),
        title: getLabelErr(err, tr, staticCommonLabelKeys.LABEL_LEGAL_ERR, staticCommonLabelKeys.COMMON_LABEL_LEGAL),
      },
      {
        ariaLabel: getLabelErr(
          err,
          tr,
          staticCommonLabelKeys.LABEL_ACCESSIBILITY_ERR,
          staticCommonLabelKeys.COMMON_LABEL_ACCESSIBILITY,
        ),
        href: 'https://www.manulife.ca/about-us/accessibility.html',
        id: 'accessibility',
        isExternal: true,
        label: getLabelErr(
          err,
          tr,
          staticCommonLabelKeys.LABEL_ACCESSIBILITY_ERR,
          staticCommonLabelKeys.COMMON_LABEL_ACCESSIBILITY,
        ),
        title: getLabelErr(
          err,
          tr,
          staticCommonLabelKeys.LABEL_ACCESSIBILITY_ERR,
          staticCommonLabelKeys.COMMON_LABEL_ACCESSIBILITY,
        ),
      },
      {
        ariaLabel: getLabelErr(
          err,
          tr,
          staticCommonLabelKeys.LABEL_PRIVACY_POLICY_ERR,
          staticCommonLabelKeys.COMMON_LABEL_PRIVACY_POLICY,
        ),
        href: 'https://www.manulife.ca/privacy-policies.html',
        id: 'privacyPolicy',
        isExternal: true,
        label: getLabelErr(
          err,
          tr,
          staticCommonLabelKeys.LABEL_PRIVACY_POLICY_ERR,
          staticCommonLabelKeys.COMMON_LABEL_PRIVACY_POLICY,
        ),
        title: getLabelErr(
          err,
          tr,
          staticCommonLabelKeys.LABEL_PRIVACY_POLICY_ERR,
          staticCommonLabelKeys.COMMON_LABEL_PRIVACY_POLICY,
        ),
      }]}
      copyrightYear=""
      copyrightYearAria=""
      copyrightText={getLabelErr1(err, tr, curYear).replace('1999-', `1999-${curYear}`)}
    />
  </section>
);

function LayoutSubNavItem({ item, handleRedirect, availMenus }) {
  const onClick = useCallback(() => {
    handleRedirect(item);
  }, [item, handleRedirect]);
  return (
    <>
      {/* Use menuId for DB lookup if available, fallback to id for backward compatibility */}
      {availMenus.includes(item.menuId || item.id) && (
        <SubNavItem
          as="button"
          id={item.id}
          key={item.id}
          itemKey={item.id}
          label={item.label}
          onClick={onClick}
          toggleOnClick
        />
      )}
    </>
  );
}
const modalSection = ({ modalOpen, modalClose, _handleConfirm, handleModalConfirm, handleModalClose }) => (
  <Modal
    isOpen={modalOpen}
    onClose={modalClose}
    ariaLabel="Confirmation"
    ariaLabelledBy="heading"
    ariaDescribedBy="content"
    shouldCloseOnBackdropClick={false}
    shouldCloseWithEscapeKey={false}
    showCloseButton
  >
    <div>
      <div className="fasatConfirmMsg f-align-left" data-testid="FasatConfirmationMsgComponent">
        {t(staticCommonLabelKeys.COMMON_UNSAVED_WARNING)}
      </div>
      <div className="buttonContainer">
        <div className="left">
          <FasatButton variant="secondary" id="modalSecondaryButton" type="button" onClick={handleModalConfirm}>
            {t(staticCommonLabelKeys.COMMON_BUTTON_LEAVE)}
          </FasatButton>
        </div>
        <div className="right">
          <FasatButton type="submit" id="modalPrimaryButton" onClick={handleModalClose}>
            {t(staticCommonLabelKeys.COMMON_BUTTON_RETURNTOPAGE)}
          </FasatButton>
        </div>
      </div>

    </div>
  </Modal>
);
const modalWidth = '660px';
const modallogoutSection = ({ signOutmodal, modalCloselog, _clickSignInOut, _setSignOutModal, handleSetSignOutModal, handleclickSignInOut }) => (
  <Modal
    isOpen={signOutmodal}
    onClose={modalCloselog}
    ariaLabel="Confirmation"
    ariaLabelledBy="heading"
    ariaDescribedBy="content"
    shouldCloseOnBackdropClick={false}
    shouldCloseWithEscapeKey={false}
    customStyle={{ modalStyle: { width: modalWidth } }}
    showCloseButton
  >
    <div>
      <span style={{ fontWeight: 900 }}>
        {t(staticCommonLabelKeys.CIAM_LOGOUT_DEFAULT)}
      </span>
      <p>{t(staticCommonLabelKeys.CIAM_LOGOUT_DEFAULT_WARNING)}</p>
      <div className="buttonContainer">
        <div className="left">
          <FasatButton
            type="submit"
            variant="secondary"
            id="modalPrimaryButton"
            onClick={handleSetSignOutModal}
          >
            {t(staticCommonLabel.CIAM_SIGN_IN)}
          </FasatButton>
        </div>
        <div className="right">
          <FasatButton
            // variant="secondary"
            id="modalSecondaryButton"
            type="button"
            onClick={handleclickSignInOut}
          >
            {t(staticCommonLabel.CIAM_SIGN_OUT)}
          </FasatButton>
        </div>
      </div>

    </div>
  </Modal>
);
const modalSectionSignout = ({ signmodalOpen, signmodalClose, _handleConfirmSignOut, handleSignModalClose, handleModalConfirmSignOut }) => (
  <Modal
    isOpen={signmodalOpen}
    onClose={signmodalClose}
    ariaLabel="Confirmation"
    ariaLabelledBy="heading"
    ariaDescribedBy="content"
    shouldCloseOnBackdropClick={false}
    shouldCloseWithEscapeKey={false}
    showCloseButton
  >
    <div>
      <div className="fasatConfirmMsg f-align-left" data-testid="FasatConfirmationMsgComponent">
        {t(staticCommonLabelKeys.COMMON_UNSAVED_WARNING)}
      </div>
      <div className="buttonContainer">
        <div className="left">
          <FasatButton
            variant="secondary"
            id="modalSecondaryButton"
            type="button"
            onClick={handleModalConfirmSignOut}
          >
            {t(staticCommonLabelKeys.COMMON_BUTTON_LEAVE)}
          </FasatButton>
        </div>
        <div className="right">
          <FasatButton type="submit" id="modalPrimaryButton" onClick={handleSignModalClose}>
            {t(staticCommonLabelKeys.COMMON_BUTTON_RETURNTOPAGE)}
          </FasatButton>
        </div>
      </div>
    </div>
  </Modal>
);
const modalLanChange = ({ lanModalOpen, langModalClose, _handleLangChange, handlelangModalClose, onClickhandleLangChange }) => (
  <Modal
    isOpen={lanModalOpen}
    onClose={langModalClose}
    ariaLabel="Confirmation"
    ariaLabelledBy="heading"
    ariaDescribedBy="content"
    shouldCloseOnBackdropClick={false}
    shouldCloseWithEscapeKey={false}
    showCloseButton
    modalWidth="560px"
  >
    <div>
      <div className="fasatConfirmMsg f-align-left" data-testid="FasatConfirmationMsgComponent">
        {t(staticCommonLabelKeys.COMMON_UNSAVED_WARNING)}
      </div>
      <div className="buttonContainer">
        <div className="left">
          <FasatButton variant="secondary" id="modalSecondaryButton" type="button" onClick={onClickhandleLangChange}>
            {t(staticCommonLabelKeys.COMMON_BUTTON_LEAVE)}
          </FasatButton>
        </div>
        <div className="right">
          <FasatButton type="submit" id="modalPrimaryButton" onClick={handlelangModalClose}>
            {t(staticCommonLabelKeys.COMMON_BUTTON_RETURNTOPAGE)}
          </FasatButton>
        </div>
      </div>

    </div>
  </Modal>
);
const onClickDiscard = (setCloseAddEditModal, closeAddEditModal, _history, dispatch) => {
  setCloseAddEditModal(!closeAddEditModal);
  dispatch(checkDirtyData(false));
};
const onCancelClickCancel = (setCancelDisplayModal, cancelDisplayModal) => {
  setCancelDisplayModal(!cancelDisplayModal);
  const saveFilterModal = document.getElementsByClassName('saveFilterModal');
  if (saveFilterModal.length > 0) {
    document.querySelector('.saveFilterModal').parentElement.style.zIndex = '99996';
  }
};
function DisplayCRModal({ args }) {
  const { cancelDisplayModal, setCancelDisplayModal, setCloseAddEditModal,
    closeAddEditModal, history, dispatch, setLvClq } = args;
  const handleonCancelClickCancel = useCallback(() => {
    onCancelClickCancel(setCancelDisplayModal, cancelDisplayModal);
  }, [onCancelClickCancel, setCancelDisplayModal]);
  const handleonClickDiscard = useCallback(() => {
    setLvClq(true);
    onClickDiscard(setCloseAddEditModal, closeAddEditModal, history, dispatch);
  }, [setLvClq, onClickDiscard]);
  return (
    <FasatModal
      display={cancelDisplayModal}
      modalWidth="560px"
      primaryButtonText={t(staticCommonLabelKeys.COMMON_BUTTON_RETURNTOPAGE)}
      primaryTopic="ON_SAVE_STAY"
      secondaryButtonText={t(staticCommonLabelKeys.COMMON_BUTTON_LEAVE)}
      secondaryTopic="ON_CANCEL_LEAVE"
      openModal="OPEN_ADD_GET_MODAL"
      onModalClose={handleonCancelClickCancel}
      staydiscard
      onClickDiscard={handleonClickDiscard}
      modalClass="cancelPopup modalWithoutHeading"
    >
      <div><CancelPopup onDiscardTopic="ON_SAVE_STAY" /></div>
    </FasatModal>
  );
}
const modalLangPref = ({ langPrefModalOpen, closeAddEditModal, userlang, handleonCancelClickCancel, handlesetlangPrefModalOpen }) => (
  <FasatModal
    title={t(staticCommonLabelKeys.COMMON_LABEL_USER_PREF_LANG_TITLE)}
    display={langPrefModalOpen}
    modalWidth="560px"
    primaryButtonText={t(staticCommonLabelKeys.COMMON_BUTTON_SAVE)}
    secondaryButtonText={t(staticCommonLabelKeys.COMMON_BUTTON_CANCEL)}
    primaryTopic={USER_LAN_SAVE}
    secondaryTopic={USER_LAN_CANCEL}
    modalClass="saveFilterModal"
    onModalClose={handlesetlangPrefModalOpen}
    isCheckDirty="yes"
    onCancelClickCancel={handleonCancelClickCancel}
  >
    <UserPrefLang
      onSaveTopic={USER_LAN_SAVE}
      onCancelTopic={USER_LAN_CANCEL}
      formData={{ userLang: userlang }}
      closeAddEditModal={closeAddEditModal}
    />
  </FasatModal>
);
export const langChangeFunc = ({ isDirtyCheck, setLabel, setlanModalOpen, setLocalLanguage, lang }) => {
  if (isDirtyCheck) {
    setLabel(lang);
    setlanModalOpen(true);
  } else {
    setLocalLanguage(lang);
  }
};
export const loggedUserCheck = ({ loggedUser, rbacData, setIsSignedInValue }) => {
  if (loggedUser?.UserName && rbacData?.role) {
    setIsSignedInValue(true);
  }
};
export const clickSignInOutFunc = ({ isSignedInValue, loggedUser, dispatch, userType }) => {
  if (isSignedInValue) {
    if (userType === 'external') {
      dispatch(releaseLockCombo({ ...userLock, lockedBy: loggedUser.UserId }));
      window.location = `${loggedUser.LogOutUrl}&&logOutType=self`;
    } else {
      dispatch(releaseLockCombo({ ...userLock, lockedBy: loggedUser.UserId }));
      window.location = loggedUser.LogOutUrl;
    }
  } else {
    window.location = 'bff/login';
    clearToken();
  }
};
export const handleSignIn = () => {
  window.location.pathname = '/';
  clearToken();
};
export const redirectPathFunc = ({ location, isDirtyCheck, item, setModalOpen, setHasPath, history }) => {
  if (location.pathname !== '/' && isDirtyCheck) {
    if (item.urlPath !== '#' && (location.pathname !== item.urlPath)) {
      setModalOpen(true);
      setHasPath(item.urlPath);
    }
  } else {
    history(item.urlPath);
  }
};
export const signInDirtyFunc = ({ isDirtyCheck, setsignmodalOpen, setSignOutModal, loggedUser }) => {
  if (isDirtyCheck) {
    setsignmodalOpen(true);
  } else if (loggedUser.UserType === 'external') {
    setSignOutModal(true);
  } else {
    localStorage.setItem('logInTime', '');
    window.location = loggedUser.LogOutUrl;
    clearToken();
  }
};
export const handleConfirmfunc = ({ history, setModalOpen, dispatch, hasPath }) => {
  history(hasPath);
  setModalOpen(false);
  dispatch(checkDirtyData(false));
};
export const modalCloseFunc = ({ setHasPath, setModalOpen }) => {
  setHasPath(null);
  setModalOpen(false);
};
// Need to remove after User Pref Lang inplementation
export const langCheckFunction = ({ lang }) => setCurrentLocale(lang.split('-')[0]);
export const langChangeFuncSecond = ({ setLocalLanguage, setlanModalOpen, dispatch, label }) => {
  setLocalLanguage(label);
  setlanModalOpen(false);
  dispatch(checkDirtyData(false));
};
export const subChildMenuExit = (item, availMenus) => {
  for (const child of item) {
    // Use menuId for DB lookup if available, fallback to id for backward compatibility
    if (availMenus.includes(child.menuId || child.id)) { return true; }
  }
  return false;
};
export const subMenuExist = (item, availMenus) => {
  for (const subItem of item.subMenu) {
    let response = false;
    if (subItem.subMenu) {
      response = subChildMenuExit(subItem.subMenu, availMenus);
    // Use menuId for DB lookup if available, fallback to id for backward compatibility
    } else if (availMenus.includes(subItem.menuId || subItem.id)) {
      response = true;
    } else {
      logger.verbose('fix sonar issue');
    }
    if (response) {
      return true;
    }
  }
  return false;
};

export function LayoutSubMenu({ item, index, availMenus, handleRedirect }) {
  return (
    <MainNavItem
      as="button"
      icon={item.icon}
      id={item.id}
      itemKey={index}
      label={t(item.label)}
      key={item.id}
    >
      {item.subMenu.map((submenu) => {
        if (submenu.subMenu) {
          const hasSubSMenuExist = subMenuExist(submenu, availMenus);
          if (hasSubSMenuExist) {
            return (
              <SubNavDropdown
                as="button"
                id={submenu.id}
                itemKey={submenu.id}
                label={submenu.label}
                key={item.id}
                toggleOnClick
              >
                {
                  submenu.subMenu?.length > 0
                  && submenu.subMenu.map((dropItem) => (
                    <LayoutSubNavItem
                      item={dropItem}
                      handleRedirect={handleRedirect}
                      availMenus={availMenus}
                      key={dropItem.id}
                    />
                  ))
}
              </SubNavDropdown>
            );
          }
          return <></>;
        }
        return (<LayoutSubNavItem item={submenu} key={submenu.id} handleRedirect={handleRedirect} availMenus={availMenus} />);
      })}
    </MainNavItem>
  );
}

export function LayoutMenuItem({ menuItems, handleRedirect, availMenus }) {
  return (
    <>
      {menuItems.map((item, index) => {
        const hasSubMenuExist = subMenuExist(item, availMenus);
        if (item.subMenu.length > 0 && hasSubMenuExist) {
          return (
            <LayoutSubMenu
              item={item}
              index={index}
              availMenus={availMenus}
              handleRedirect={handleRedirect}
              key={item.id}
            />
          );
        }
        if (item.subMenu.length <= 0) {
          return (
            <MainNavItem
              as="button"
              icon={item.icon}
              id={item.id}
              key={item.id}
              itemKey={index}
              label={t(item.label)}
              onClick={handleRedirect}
            />
          );
        }

        return <></>;
      })}
    </>
  );
}
const pathLocation = (location) => location && location.pathname !== '/';

const validateLock = ({ lockDetails, loggedUser }) => lockDetails && loggedUser && !lockDetails.listToDetail;
export const getSideNavWrapperClass = (loggedUser, rbacData) => (loggedUser && !rbacData ? 'noAccessNav' : '');
const stateCheck = (location) => (typeof location.state === 'object' && !('transPolBal' in location.state))
|| typeof location.state === 'string';
const releaseLock = ({ lockDetails, loggedUser, dispatch, location }) => {
  if (validateLock({ lockDetails, loggedUser })) {
    let rcdKey = lockDetails.recordKey;
    let entKey = lockDetails.entityKey;
    const action = lockDetails.action || 'edit';
    if (lockDetails.lockCombo && lockDetails.lockCombo.recordKey) {
      rcdKey = lockDetails.lockCombo.recordKey;
      entKey = lockDetails.lockCombo.entityKey;
    }
    if ((!location.state || stateCheck(location)) && location?.state?.path !== 'editCompCal') {
      dispatch(releaseLockCombo({ ...userLock,
        lockedBy: loggedUser.UserId,
        recordKey: rcdKey,
        entityKey: entKey,
        action: rcdKey ? action : '',
      }));
    }
  }
  if (pathLocation(location)) {
    dispatch(getBatchProcessingState());
  }
};
const sendEmail = () => {
  window.location = 'mailto:comp_mfc@manulife.ca';
};
const dialNum = () => {
  window.location = 'tel:1-800-838-7770';
};

function HeaderSection({ args }) {
  const { loggedUser, rbacData, isDirtyCheck, setLabel, setsignmodalOpen,
    setlangPrefModalOpen, setlanModalOpen, setSignOutModal, setLocalLanguage } = args;
  const handlelangChangeFunc = useCallback((lang) => {
    langChangeFunc({ isDirtyCheck, setLabel, setlanModalOpen, setLocalLanguage, lang });
  }, [langChangeFunc, setLabel, setlanModalOpen, setLocalLanguage]);
  const handlesignInDirtyFunc = useCallback(() => {
    signInDirtyFunc({ isDirtyCheck, setsignmodalOpen, setSignOutModal, loggedUser });
  }, [setsignmodalOpen, setSignOutModal, loggedUser]);
  const handlesetlangPrefModalOpen = useCallback(() => {
    setlangPrefModalOpen(true);
  }, [setlangPrefModalOpen]);
  return (
    <HeaderWrapper>
      <UtilityHeader variant={HEADER_VARIANT.MAIN}>
        {!(loggedUser && rbacData?.role) && (<SignInButton onClick={handleSignIn} />)}
        {(loggedUser && rbacData?.role) && (
        <HeaderButton label="Profile" id="login-icon" icon={<Login1 aria-hidden="true" />} showMenuArrow={false}>
          <MenuItem label="Language preference" onClick={handlesetlangPrefModalOpen} />
          <MenuItem
            label={t(staticCommonLabelKeys.COMMON_BUTTON_SIGNOUT)}
            onClick={handlesignInDirtyFunc}
          />
        </HeaderButton>
        )}
        <LangButton
          id="langbtn"
          onClick={handlelangChangeFunc}
          // aria-label={locale === 'en' ? 'English' : 'Français'}
        />
      </UtilityHeader>
    </HeaderWrapper>
  );
}

HeaderSection.propTypes = {
  args: PropTypes.shape({
    loggedUser: PropTypes.object,
    rbacData: PropTypes.shape({
      role: PropTypes.any,
    }),
    isDirtyCheck: PropTypes.bool,
    setLabel: PropTypes.func,
    setsignmodalOpen: PropTypes.func,
    setlangPrefModalOpen: PropTypes.func,
    setlanModalOpen: PropTypes.func,
    setSignOutModal: PropTypes.func,
    setLocalLanguage: PropTypes.func,
  }).isRequired,
};

const initLogedUser = ({ loggedUser, dispatch, location }) => {
  if (loggedUser && loggedUser.UserName && location.pathname !== '/') {
    dispatch(getRBACData());
    dispatch(getLevelRBACData());
  }
  if (loggedUser && loggedUser.UserName) {
    dispatch(getUserPrefData());
    dispatch(getSystemConfig(sysConfigPayload));
    dispatch(getUserContactInfo());
  }
};

const resetUsrLanMdl = ({ cancelDisplayModal, setlangPrefModalOpen, lvClq, setLvClq }) => {
  if (!cancelDisplayModal && lvClq) {
    setlangPrefModalOpen(false);
    setLvClq(false);
  }
};

const renderMenuArr = ({ allQuicksLinks, isBatchProcessing }) => {
  const arr = [];
  if (allQuicksLinks && allQuicksLinks.length > 0) {
    allQuicksLinks.forEach((item) => {
      if (isBatchProcessing === 1 && (item.batch_Online_Concurrency_Flag === 1)) {
        arr.push(item.menuId);
      }
      if (!isBatchProcessing) {
        arr.push(item.menuId);
      }
    });
  }
  return arr;
};

const initialValidation = (dispatch) => {
  if (window.location.pathname !== '/sign-out') {
    dispatch(usersDataProcessing());
  }
};
// mobile friendly header

const checkSignin = ({ loggedUser, rbacData, _setlangPrefModalOpen, _isDirtyCheck, _setsignmodalOpen, _setSignOutModal, handleCheckSignin, handlesetlangPrefModalOpen }) => {
  if (loggedUser && rbacData && rbacData.role) {
    return (
      <SideNav.HeaderButton label="Profile" id="login-icon" icon={<Login1 aria-hidden="true" />} showMenuArrow={false}>
        <SideNav.MenuItem label="Language preference" onClick={handlesetlangPrefModalOpen} />
        <SideNav.MenuItem
          label={t(staticCommonLabelKeys.COMMON_BUTTON_SIGNOUT)}
          onClick={handleCheckSignin}
        />
        <SideNav.MenuItem label="" />
      </SideNav.HeaderButton>
    );
  }
  return (<SideNav.SignIn onClick={handleSignIn} />);
};

const contactFooterButtonStyle = {
  color: '#fff',
  'text-decoration': 'none',
  'font-size': '19px',
  'font-weight': '700',
  'padding-top': '0',
};

const contactFooterActionBtnStyle = {
  buttonStyle: contactFooterButtonStyle,
  iconStyle: {
    'margin-right': '10px',
    ...contactFooterButtonStyle,
  },
};
function Layout({ menuItems, children }) {
  const [label, setLabel] = useState('');
  const [isSignedInValue, setIsSignedInValue] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [signmodalOpen, setsignmodalOpen] = useState(false);
  const [lanModalOpen, setlanModalOpen] = useState(false);
  const [langPrefModalOpen, setlangPrefModalOpen] = useState(false);
  const [signOutmodal, setSignOutModal] = useState(false);
  const [availMenus, setAvailMenus] = useState([]);
  const [hasPath, setHasPath] = useState(null);
  const [closeAddEditModal, setCloseAddEditModal] = useState(false);
  const [cancelDisplayModal, setCancelDisplayModal] = useState(false);
  const [lvClq, setLvClq] = useState(false);
  const history = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const handleConfirm = () => handleConfirmfunc({ history, setModalOpen, dispatch, hasPath });
  const modalClose = () => modalCloseFunc({ setHasPath, setModalOpen });
  const modalCloselog = () => setSignOutModal(false);
  const setLocalLanguage = (lang) => langCheckFunction({ lang });
  const isDirtyCheck = useSelector((state) => state.ApplicationState.isDirty);
  const { lockDetails, isBatchProcessing, allQuicksLinks } = useSelector((state) => state.ApplicationState);
  const { userPrefError, rbacData, loggedUser, userlang, userContactInfo } = useSelector(({
    ApplicationStateRBAC }) => ApplicationStateRBAC);
  const userType = useSelector(({ ApplicationStateRBAC }) => ApplicationStateRBAC?.loggedUser?.UserType);
  const signmodalClose = () => setsignmodalOpen(false);
  const langModalClose = () => setlanModalOpen(false);
  useEffect(() => { loggedUserCheck({ loggedUser, rbacData, setIsSignedInValue }); }, [loggedUser, rbacData]);
  useEffect(() => { setCurrentLocale(userlang === 'E' ? 'en' : 'fr'); }, [userlang]);
  useEffect(() => { initialValidation(dispatch); }, []);
  useEffect(() => { releaseLock({ lockDetails, loggedUser, dispatch, location }); }, [location, loggedUser]);
  const clickSignInOut = () => clickSignInOutFunc({ isSignedInValue, loggedUser, dispatch, userType });
  const handleConfirmSignOut = () => {
    clickSignInOut();
    dispatch(checkDirtyData(false));
  };
  const handleLangChange = () => langChangeFuncSecond({ setLocalLanguage, setlanModalOpen, dispatch, label });
  const redirectPath = (item) => {
    dispatch(resetStore());
    redirectPathFunc({ location, isDirtyCheck, item, setModalOpen, setHasPath, history });
  };
  const handleNavItemClick = useCallback((item) => {
    if (item && !item.submenu) {
      redirectPath(item);
    }
  }, [redirectPath]);
  useEffect(() => { initLogedUser({ loggedUser, dispatch, location }); }, [loggedUser]);
  useEffect(() => { resetUsrLanMdl({ cancelDisplayModal, setlangPrefModalOpen, lvClq, setLvClq }); }, [cancelDisplayModal]);
  useEffect(() => { if (allQuicksLinks) { setAvailMenus(renderMenuArr({ allQuicksLinks, isBatchProcessing })); } }, [allQuicksLinks]);
  const sideNavWrapperClass = getSideNavWrapperClass(loggedUser, rbacData);
  const { mainContentRef } = useSkipLinks();
  const handleSendEmail = useCallback(() => sendEmail(), [sendEmail]);
  const handleDialNum = useCallback(() => dialNum(), [dialNum]);
  const handlelangChangeFunc = useCallback((lang) => { langChangeFunc({ isDirtyCheck, setLabel, setlanModalOpen, setLocalLanguage, lang }); }, [langChangeFunc, isDirtyCheck]);
  return (
    <UserContext.Provider value={userType}>
      <LayoutWrapper>
        <HeaderSection args={{ loggedUser, rbacData, isDirtyCheck, setLabel, setsignmodalOpen, setlangPrefModalOpen, setlanModalOpen, setSignOutModal, setLocalLanguage }} />
        <SideNavWrapper className={sideNavWrapperClass}>
          <SideNav
            brandLogoProps={{ onClick: () => redirectPath({ urlPath: '/' }), alt: t(staticCommonLabel.ARIA_BRAND_LOGO_ALT) }}
            keepOpenOnSelect={false}
            mobileFooterItems={[checkSignin({ loggedUser, rbacData, setlangPrefModalOpen, isDirtyCheck, setsignmodalOpen, setSignOutModal, handleCheckSignin: () => signInDirtyFunc({ isDirtyCheck, setsignmodalOpen, setSignOutModal, loggedUser }), handlesetlangPrefModalOpen: () => setlangPrefModalOpen(true) }), <SideNav.Lang
              key="lang"
              onClick={handlelangChangeFunc}
            />]}
            className="sideNav"
          >
            {availMenus.length > 0
            && <LayoutMenuItem menuItems={menuItems} handleRedirect={handleNavItemClick} availMenus={availMenus} /> }
          </SideNav>
          {modallogoutSection({ signOutmodal,
            modalCloselog,
            clickSignInOut,
            setSignOutModal,
            handleSetSignOutModal: () => setSignOutModal(false),
            handleclickSignInOut: () => {
              clickSignInOut();
              localStorage.setItem('logInTime', '');
              setSignOutModal(false);
            } })}
          {modalSection({ modalOpen, modalClose, handleConfirm, handleModalConfirm: () => handleConfirm(), handleModalClose: () => modalClose() })}
          {modalSectionSignout({ signmodalOpen, signmodalClose, handleConfirmSignOut, handleSignModalClose: () => signmodalClose(), handleModalConfirmSignOut: () => handleConfirmSignOut() })}
          {modalLanChange({ lanModalOpen, langModalClose, handleLangChange, handlelangModalClose: () => langModalClose(), onClickhandleLangChange: () => handleLangChange() })}
          {modalLangPref({ langPrefModalOpen, setlangPrefModalOpen, closeAddEditModal, setCancelDisplayModal, userlang, cancelDisplayModal, handleonCancelClickCancel: () => onCancelClickCancel(setCancelDisplayModal, cancelDisplayModal), handlesetlangPrefModalOpen: () => setlangPrefModalOpen(false) })}
          <DisplayCRModal args={{ cancelDisplayModal,
            setCancelDisplayModal,
            setCloseAddEditModal,
            closeAddEditModal,
            history,
            dispatch,
            setLvClq }}
          />
        </SideNavWrapper>
        <LayoutContainer ref={mainContentRef} className="mainLayoutContainer" tabIndex="-1">
          <section data-skip-link="Skip to Content" id="landing-page-insurance-link">
            <LayoutContent>{children}</LayoutContent>
          </section>
          <div className="fasatFooter">
            {(userType === external && userContactInfo) ? (
              <div className="fasatContactUsFooter">
                <div className="contactUsFooter">
                  <FasatActionBtn
                    icon={(<Phone aria-hidden="true" color={colors.white} width="20px" height="20px" customStyle={{ buttonStyle: { color: '#1E212F' } }} />)}
                    customStyle={contactFooterActionBtnStyle}
                    customStyle2={{ buttonStyle: { color: 'red' } }}
                    onClick={handleDialNum}
                  >
                    {' '}
                    1-800-838-7770
                  </FasatActionBtn>
                </div>
                <div className="contactUsFooter">
                  <FasatActionBtn
                    icon={(<Email aria-hidden="true" color={colors.white} width="20px" height="20px" />)}
                    onClick={handleSendEmail}
                    customStyle={contactFooterActionBtnStyle}
                  >
                    comp_mfc@manulife.ca
                  </FasatActionBtn>
                </div>
              </div>
            ) : ' '}
            {getFooter({ userPrefError, t })}
          </div>
        </LayoutContainer>
      </LayoutWrapper>
    </UserContext.Provider>
  );
}
Layout.propTypes = {
  menuItems: PropTypes.array.isRequired,
  children: PropTypes.object.isRequired,
};
export default Layout;
